import { useCallback, useEffect, useState } from 'react';
import axios from '../utils/axios';
import { validColoradoPotencyMapping } from '../utils/coloradoPotencyMapping';

export type BatchTestAnalyte = {
  key: string;
  resultId: number;
  detailId: number;
  testTypeId: number | null;
  name: string;
  testingGroupName: string | null;
  batchName: string | null;
  passed: boolean | null;
  value: number | null;
  unit: string | null;
  qualifier: string | null;
  isRevoked: boolean | null;
  detailIsRevoked: boolean | null;
  informationalOnly: boolean | null;
  sourcePackageId: number | null;
  sourcePackageLabel: string | null;
  documentId: number | null;
  lastModified: string | null;
  releasedAt: string | null;
  valueSource: string;
  unitSource: string | null;
  analyteKey?: string | null;
  measurementBasis?: 'percent' | 'perUnit' | 'perServing' | null;
  aggregateKind?: 'active' | 'total' | null;
  mappingStatus?: 'mapped' | 'unitUnavailable' | 'identityMismatch' | 'unmapped' | null;
  mappingSource?: string | null;
};

export type BatchTestBatch = {
  key: string;
  resultId: number;
  name: string | null;
  laboratoryName: string | null;
  laboratoryLicense: string | null;
  testedDate: string | null;
  overallPassed: boolean | null;
  isRevoked: boolean | null;
  documentIds: number[];
  analytes: BatchTestAnalyte[];
};

export type BatchTestCategory = {
  name: string;
  kind: 'potency' | 'contaminants' | 'other';
  batches: BatchTestBatch[];
  requirement?: 'required' | 'mayBeRequired' | 'notRequired' | null;
  status?: 'Passed' | 'Failed' | 'Not Tested' | 'Unavailable' | null;
  analyteCount?: number | null;
};

export type PotencyDisplayPolicy = {
  version: string;
  marketCode: 'CO';
  selectedAnalyteKeys: string[];
};

const potencyAnalyteKeys = new Set(['activeCbd', 'activeThc', 'cbd', 'cbda', 'cbn', 'delta10thc',
  'delta8thc', 'delta9thc', 'delta9thca', 'exothc', 'totalCbd', 'totalThc']);

export type BatchTestResults = {
  potencyDisplayPolicy?: PotencyDisplayPolicy | null;
  availability: 'available' | 'empty' | 'unavailable';
  reportedCount: number;
  omittedCount: number;
  categories: BatchTestCategory[];
  matrixVersion?: string | null;
  itemCategory?: string | null;
  overallStatus?: 'Passed' | 'Failed' | null;
};

export const BATCH_TEST_RESULTS_ERROR = 'Batch test results could not be loaded.';

const record = (input: unknown): input is Record<string, unknown> => (
  typeof input === 'object' && input !== null && !Array.isArray(input)
);
const nullableString = (input: unknown) => input === null || typeof input === 'string';
const nullableBoolean = (input: unknown) => input === null || typeof input === 'boolean';
const finiteNumber = (input: unknown): input is number => typeof input === 'number' && Number.isFinite(input);
const nullableNumber = (input: unknown) => input === null || finiteNumber(input);
const enumString = (input: unknown, values: readonly string[]): input is string => (
  typeof input === 'string' && values.includes(input)
);

function isAnalyte(input: unknown): input is BatchTestAnalyte {
  return record(input)
    && typeof input.key === 'string' && typeof input.name === 'string'
    && finiteNumber(input.resultId) && finiteNumber(input.detailId)
    && ['testTypeId', 'sourcePackageId', 'documentId', 'value'].every(key => nullableNumber(input[key]))
    && ['testingGroupName', 'batchName', 'unit', 'qualifier', 'sourcePackageLabel', 'unitSource', 'lastModified', 'releasedAt']
      .every(key => nullableString(input[key]))
    && ['passed', 'isRevoked', 'detailIsRevoked', 'informationalOnly'].every(key => nullableBoolean(input[key]))
    && typeof input.valueSource === 'string' && validColoradoPotencyMapping(input);
}

function isBatch(input: unknown): input is BatchTestBatch {
  return record(input)
    && typeof input.key === 'string' && finiteNumber(input.resultId)
    && ['name', 'laboratoryName', 'laboratoryLicense', 'testedDate'].every(key => nullableString(input[key]))
    && nullableBoolean(input.overallPassed)
    && nullableBoolean(input.isRevoked)
    && Array.isArray(input.documentIds) && input.documentIds.every(finiteNumber)
    && Array.isArray(input.analytes) && input.analytes.every(isAnalyte);
}

function isCategory(input: unknown): input is BatchTestCategory {
  return record(input) && typeof input.name === 'string'
    && (input.kind === 'potency' || input.kind === 'contaminants' || input.kind === 'other')
    && Array.isArray(input.batches) && input.batches.every(isBatch)
    && (input.requirement == null || enumString(input.requirement, ['required', 'mayBeRequired', 'notRequired']))
    && (input.status == null || enumString(input.status, ['Passed', 'Failed', 'Not Tested', 'Unavailable']))
    && (input.analyteCount == null || (finiteNumber(input.analyteCount) && Number.isInteger(input.analyteCount) && input.analyteCount >= 0));
}

export function isBatchTestResults(input: unknown, marketCode?: string): input is BatchTestResults {
  if (!record(input)
    || !enumString(input.availability, ['available', 'empty', 'unavailable'])
    || !finiteNumber(input.reportedCount) || !Number.isInteger(input.reportedCount) || input.reportedCount < 0
    || !finiteNumber(input.omittedCount) || !Number.isInteger(input.omittedCount) || input.omittedCount < 0
    || !Array.isArray(input.categories) || !input.categories.every(isCategory)) return false;
  if (input.potencyDisplayPolicy != null) {
    const policy = input.potencyDisplayPolicy;
    if (!record(policy) || typeof policy.version !== 'string' || !policy.version.trim()
      || policy.marketCode !== 'CO' || (marketCode != null && marketCode.trim().toUpperCase() !== 'CO')
      || !Array.isArray(policy.selectedAnalyteKeys)
      || !policy.selectedAnalyteKeys.every(key => typeof key === 'string' && potencyAnalyteKeys.has(key))
      || new Set(policy.selectedAnalyteKeys).size !== policy.selectedAnalyteKeys.length) return false;
  }
  if (input.matrixVersion != null) {
    if (typeof input.matrixVersion !== 'string' || !input.matrixVersion.trim()
      || typeof input.itemCategory !== 'string' || !input.itemCategory.trim()
      || (input.overallStatus !== null && !enumString(input.overallStatus, ['Passed', 'Failed']))
      || (input.overallStatus === 'Passed' && (input.reportedCount === 0
        || input.categories.some(category => category.requirement === 'required'
          && (category.status === 'Not Tested' || category.status === 'Unavailable'
            || (category.kind === 'potency' && category.batches.some(batch => batch.analytes.some(analyte => (
              analyte.value == null || !analyte.unit?.trim()
              || ['unitUnavailable', 'identityMismatch', 'unmapped'].includes(analyte.mappingStatus ?? '')
            ))))))))
      || !input.categories.every(category => {
        const count = category.batches.reduce((total, batch) => total + batch.analytes.length, 0);
        const hasPassedContradiction = category.batches.some(batch => batch.overallPassed === false || batch.isRevoked === true
          || batch.analytes.some(analyte => analyte.passed === false || analyte.isRevoked === true || analyte.detailIsRevoked === true));
        const hasRevokedEvidence = category.batches.some(batch => batch.isRevoked === true
          || batch.analytes.some(analyte => analyte.isRevoked === true || analyte.detailIsRevoked === true));
        return category.requirement !== undefined && category.status != null && category.analyteCount === count
          && (count === 0
            ? category.requirement === 'required' && category.status === 'Not Tested' && category.batches.length === 0
            : category.status !== 'Not Tested')
          && !(hasPassedContradiction && category.status === 'Passed')
          && !(hasRevokedEvidence && input.overallStatus === 'Passed')
          && (category.status !== 'Passed' || category.batches.every(batch => batch.analytes.every(analyte => analyte.passed === true)));
      })) return false;
  } else if (input.itemCategory != null || input.overallStatus != null
    || input.categories.some(category => category.requirement != null || category.status != null || category.analyteCount != null)) return false;
  const count = input.categories.reduce((total, category) => (
    total + category.batches.reduce((subtotal, batch) => subtotal + batch.analytes.length, 0)
  ), 0);
  return count === input.reportedCount
    && (input.availability === 'available' ? count > 0
      : input.availability === 'empty' ? count === 0 && input.omittedCount === 0
      : count === 0);
}

export async function getPublicBatchTestResults(
  issuanceId: string, index: number, signal: AbortSignal, marketCode?: string,
): Promise<BatchTestResults> {
  const response = await axios.get<unknown>('/landingpage/batch-test-results', {
    params: { id: issuanceId, index }, signal, timeout: 30_000,
  });
  if (!isBatchTestResults(response.data, marketCode)) throw new Error('Invalid batch test results response.');
  return response.data;
}

export function getBatchTestDocumentUrl(issuanceId: string, index: number, documentId: number): string {
  return axios.getUri({
    url: '/landingpage/batch-test-results/document',
    params: { id: issuanceId, index, documentId },
  });
}

type RequestState = {
  key: string;
  data: BatchTestResults | null;
  isLoading: boolean;
  error: string | null;
};

export function usePublicBatchTestResults(issuanceId: string, index: number, marketCode?: string) {
  const [attempt, setAttempt] = useState(0);
  const key = JSON.stringify([issuanceId, index, marketCode, attempt]);
  const [state, setState] = useState<RequestState>({
    key, data: null, isLoading: Boolean(issuanceId), error: null,
  });
  const retry = useCallback(() => setAttempt(current => current + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    setState({ key, data: null, isLoading: Boolean(issuanceId), error: null });
    if (issuanceId) {
      getPublicBatchTestResults(issuanceId, index, controller.signal, marketCode)
        .then(data => {
          if (!controller.signal.aborted) setState({ key, data, isLoading: false, error: null });
        })
        .catch(() => {
          if (!controller.signal.aborted) setState({
            key, data: null, isLoading: false, error: BATCH_TEST_RESULTS_ERROR,
          });
        });
    }
    return () => controller.abort();
  }, [issuanceId, index, marketCode, key]);

  return state.key === key
    ? { ...state, retry }
    : { key, data: null, isLoading: Boolean(issuanceId), error: null, retry };
}

export type PublicBatchTestResultsState = ReturnType<typeof usePublicBatchTestResults>;

export function resolvePublicLabTestStatus(
  results: Pick<PublicBatchTestResultsState, 'data' | 'isLoading' | 'error'>, reportedStatus?: string,
): string | undefined {
  if (results.isLoading || results.error || !results.data || results.data.availability === 'unavailable') return undefined;
  return results.data.matrixVersion ? results.data.overallStatus ?? undefined : reportedStatus;
}
